import { GraphQlRequestService } from '../../../api/graphql-request-service';
import { Domains } from '../../constants';
import { Hasher } from '../hasher';
import { HasDependencies, hashString, Role } from '../types';
import {
  QueryCohortMetricsQuery,
  QueryEmployeeCohortMetricsQuery,
  QueryMetricsQuery,
} from '../types-graphql';
import { handleGQLErrors } from '../utils';
import {
  QueryCohortMetricsParams,
  QueryEmployeeCohortMetricsParams,
  QueryRegularMetricsParams,
} from './types';

export interface MetricsQueryService extends HasDependencies {
  queryRegularMetrics: (
    params: QueryRegularMetricsParams
  ) => Promise<QueryMetricsQuery>;
  queryCohortMetrics: (
    params: QueryCohortMetricsParams
  ) => Promise<QueryCohortMetricsQuery>;
  queryEmployeeCohortMetrics: (
    params: QueryEmployeeCohortMetricsParams
  ) => Promise<QueryEmployeeCohortMetricsQuery>;
}

export class BackendMetricsQueryService implements MetricsQueryService {
  constructor(
    readonly graphQlRequestService: GraphQlRequestService,
    readonly domain: Domains,
    readonly executorRole: Role,
    readonly simulateRole: Role | null
  ) {}

  public getDeps = () => [
    hashString([
      ...this.graphQlRequestService.getDeps(),
      this.domain,
      Hasher.hash(this.executorRole),
      Hasher.hash(this.simulateRole),
    ]),
  ];

  public queryRegularMetrics = async (
    params: QueryRegularMetricsParams
  ): Promise<QueryMetricsQuery> => {
    const { timeSelection, metrics, userFilters, grouping, disableNestLoop } =
      params;
    return handleGQLErrors(
      this.graphQlRequestService.graphQlSdk.queryMetrics({
        domain: this.domain,
        metrics: metrics.map((m) => m.value),
        disableNestLoop: disableNestLoop ?? null,
        grouping: grouping ?? null,
        timeSelection,
        userFilters: userFilters ?? null,
        selectedExecutorRole: this.executorRole.id,
        simulateRole: this.simulateRole?.id ?? null,
      })
    );
  };

  public queryCohortMetrics = async (
    params: QueryCohortMetricsParams
  ): Promise<QueryCohortMetricsQuery> => {
    const {
      timeSelection,
      metrics,
      userCohortFilters,
      disableNestLoop,
      grouping,
    } = params;
    return handleGQLErrors(
      this.graphQlRequestService.graphQlSdk.queryCohortMetrics({
        domain: this.domain,
        metrics: metrics.map((m) => m.value),
        disableNestLoop: disableNestLoop ?? null,
        grouping: grouping ?? null,
        timeSelection,
        userCohortFilters: userCohortFilters ?? null,
        selectedExecutorRole: this.executorRole.id,
        simulateRole: this.simulateRole?.id ?? null,
      })
    );
  };

  public queryEmployeeCohortMetrics = async (
    params: QueryEmployeeCohortMetricsParams
  ): Promise<QueryEmployeeCohortMetricsQuery> => {
    const {
      timeSelection,
      metrics,
      userFilters,
      userCohortFilters,
      grouping,
      disableNestLoop,
    } = params;
    return handleGQLErrors(
      this.graphQlRequestService.graphQlSdk.queryEmployeeCohortMetrics({
        domain: this.domain,
        metrics: metrics.map((m) => m.value),
        disableNestLoop: disableNestLoop ?? null,
        grouping: grouping ?? null,
        timeSelection,
        userFilters: userFilters ?? null,
        userCohortFilters: userCohortFilters ?? null,
        selectedExecutorRole: this.executorRole.id,
        simulateRole: this.simulateRole?.id ?? null,
      })
    );
  };
}
